<template>
	<v-row justify="center">
		<Disclosure :title="$t('text.bankingInfoTitle')" :error="sectionMessage.error" :message="sectionMessage.message" :lock="!userMayEditFields" data-cy="bankingInfo" ref="SECTION_bankingInfo">
			<div class="field left-border">
				<v-label>{{$t('text.accountNumber')}}</v-label>
				<v-text-field variant="outlined"
					v-model="model.fields.accountNumber.de"
					:error-messages="accountNumberErrors"
					@update:modelValue="validateAccountNumber"
					@blur="validateAccountNumber"
				/>
			</div>
			<div class="field left-border">
				<v-label>{{$t('text.bic')}}</v-label>
				<v-text-field variant="outlined"
					v-model="model.fields.bic.de"
					:error-messages="bicErrors"
					@update:modelValue="validateBic"
					@blur="validateBic"
				/>
			</div>
			<div class="field left-border">
				<v-label>{{$t('text.iban')}}</v-label>
				<v-text-field variant="outlined"
					v-model="model.fields.iban.de"
					:error-messages="ibanErrors"
					@update:modelValue="validateIban"
					@blur="validateIban"
				/>
			</div>
		</Disclosure>
	</v-row>
</template>

<script>
import Common from '@/mixins/Common.vue'
import Disclosure from '@/components/common/Disclosure.vue'
import isEqual from 'lodash/isEqual'
import { isValidAccountNumber, isValidBic, isValidIban } from '@/utils/input-validation.js'

export default {
	name: 'BankingInfo',
	components: {
		Disclosure
	},
	mixins: [ Common ],
	emits: ['validity-changed'],
	props: {
		serviceProvider: { type: Object, required: true },
		updateModel: Boolean,
		runValidation: Boolean,
	},
	data: () => ({
		model: {},
		initData: {},
		sectionMessage: {
			error: false,
			message: '',
		},
		accountNumberErrors: [],
		bicErrors: [],
		ibanErrors: [],
		lastValidityResult: null,
	}),
	watch: {
		model: {
			deep: true,
			handler() {
				const isValid = this.validateAllFields()
				if (this.lastValidityResult !== isValid) {
					this.lastValidityResult = isValid
					this.$emit('validity-changed', isValid)
				}
			}
		},
		updateModel() {
			this.model = this.valueToModel(this.serviceProvider)
			this.setInitData()
		},
	},
	methods: {
		valueToModel(v) {
			return JSON.parse(JSON.stringify(v ?? {}))
		},
		setInitData() {
			const initModel = JSON.parse(JSON.stringify(this.model))
			this.initData = {
				accountNumber: initModel.fields.accountNumber,
				bic: initModel.fields.bic,
				iban: initModel.fields.iban,
			}
		},
		sendData() {
			let data = {
				accountNumber: this.model.fields.accountNumber,
				bic: this.model.fields.bic,
				iban: this.model.fields.iban,
			}

			data.changed = !isEqual(data, this.initData)
			return data
		},
		validateAccountNumber() {
			this.accountNumberErrors = []

			if (!isValidAccountNumber(this.model.fields.accountNumber?.de)) {
				this.accountNumberErrors.push(this.$t('text.fieldValAccountNumberUnsupportedCharacters'))
			}

			return this.accountNumberErrors.length === 0
		},
		validateBic() {
			this.bicErrors = []

			if (!isValidBic(this.model.fields.bic?.de)) {
				this.bicErrors.push(this.$t('text.fieldValBicFormat'))
			}

			return this.bicErrors.length === 0
		},
		validateIban() {
			this.ibanErrors = []

			if (!isValidIban(this.model.fields.iban?.de)) {
				this.ibanErrors.push(this.$t('text.fieldValIbanFormat'))
			}

			return this.ibanErrors.length === 0
		},
		validateAllFields() {
			this.resetSectionError(this.sectionMessage)

			const allFieldsAreValid = this.validateAccountNumber() &&
				this.validateBic() &&
				this.validateIban()

			if (!allFieldsAreValid && this.runValidation) {
				this.setSectionError(this.sectionMessage, this.$t('text.missingFieldsError'))
			}

			return allFieldsAreValid
		},
	},
	created() {
		this.model = this.valueToModel(this.serviceProvider)
		this.setInitData()
	},
}
</script>

<style scoped>
</style>
